import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';

const faqs = [
  {
    id: 1,
    question: "What is fractional real estate investment?",
    answer: "Fractional real estate investment allows you to own a portion of a premium property by pooling funds with other investors. Instead of buying an entire property, you can invest in fractions starting from ₹10 Lakhs, gaining exposure to high-value real estate assets that would otherwise be out of reach for individual investors."
  },
  {
    id: 2,
    question: "How do I earn returns on my investment?",
    answer: "You earn returns through two channels: rental income and capital appreciation. Rental income is distributed proportionally among all fractional owners on a monthly or quarterly basis. When the property is sold, you receive your share of any capital gains based on your ownership percentage. Expected returns typically range from 8-22% annually."
  },
  {
    id: 3,
    question: "What is the minimum investment amount?",
    answer: "The minimum investment varies by property and asset class. For real estate, you can start with as little as ₹10 Lakhs. For fine art investments through our partnership with Masha Art, the minimum is ₹25 Lakhs. Each listing clearly displays its minimum investment requirement."
  },
  {
    id: 4,
    question: "How long is the typical investment tenure?",
    answer: "Investment tenures typically range from 18 to 36 months for real estate properties. The exact duration depends on the property type and market conditions. You'll receive regular updates on the property's performance and any exit opportunities throughout the investment period."
  },
  {
    id: 5,
    question: "Is my investment secure and regulated?",
    answer: "Yes, all investments are structured through SPVs (Special Purpose Vehicles) or LLPs (Limited Liability Partnerships) for legal clarity and investor protection. Properties are thoroughly vetted, and all documentation is legally verified. We maintain complete transparency with regular reporting and audited financials."
  }
];

export default function FAQSection() {
  const [openId, setOpenId] = useState(null);

  const toggleFAQ = (id) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section id="faq" className="py-24 bg-[#030303]">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6">
            <HelpCircle className="w-4 h-4 text-[#4ADE80]" />
            <span className="text-sm text-white/70">Got Questions?</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-light mb-4">
            <span className="text-white/40">Frequently Asked</span>{' '}
            <span className="text-white">Questions</span>
          </h2>
          <p className="text-white/50 max-w-2xl mx-auto">
            Everything you need to know about fractional investing with ALTx
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={faq.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div
                className={`bg-[#0A0A0A] border rounded-xl overflow-hidden transition-all duration-300 ${
                  openId === faq.id 
                    ? 'border-[#4ADE80]/30' 
                    : 'border-white/5 hover:border-white/10'
                }`}
              >
                {/* Question */}
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                >
                  <span className="text-white font-medium pr-4">{faq.question}</span>
                  <motion.div
                    animate={{ rotate: openId === faq.id ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex-shrink-0"
                  >
                    <ChevronDown className={`w-5 h-5 transition-colors ${
                      openId === faq.id ? 'text-[#4ADE80]' : 'text-white/40'
                    }`} />
                  </motion.div>
                </button>

                {/* Answer */}
                <AnimatePresence>
                  {openId === faq.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="px-6 pb-5 pt-0">
                        <div className="border-t border-white/5 pt-4">
                          <p className="text-white/60 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center mt-12"
        >
          <p className="text-white/40 text-sm">
            Still have questions?{' '}
            <a href="mailto:support@altx.com" className="text-[#4ADE80] hover:underline">
              Contact our support team
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
